import React, { useState, useEffect } from "react";
import { strategyChatAPI } from "../services/api";
import { fmtDateTime } from "../utils/date";

// Strategy Terminal — chat your way to a new strategy, or modify one you already
// have, in plain language. Structurally mirrors the Admin Panel's "AI Command"
// chat tab (session list + transcript + poll loop), but talks to a completely
// different, much narrower backend agent (engine/strategyChatAgent.js /
// engine/strategyChatTools.js): it can only ever see or change strategies that
// belong to YOUR OWN account, and it cannot deploy anything or place an order —
// creating/editing a strategy here works exactly like the "Create a Strategy
// with a Prompt" builder, review-then-Deploy-yourself.

// ─── Design tokens (mirror StrategyPrompt.jsx) ──────────────────────────────
const T = {
  bg: "#eef2f7", card: "#ffffff", border: "#e8edf3",
  text: "#0f172a", sub: "#64748b", muted: "#94a3b8",
  blue: "#2563eb", indigo: "#4f46e5", red: "#ef4444", green: "#10b981", field: "#f8fafc",
};
const Card = ({ children, style }) => (
  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 18, ...style }}>{children}</div>
);
const Btn = ({ children, onClick, disabled, v = "blue" }) => {
  const s = {
    blue: { bg: "linear-gradient(135deg,#4f46e5,#2563eb)", cl: "#fff", bd: "none" },
    ghost: { bg: "#fff", cl: "#475569", bd: `1px solid ${T.border}` },
  }[v];
  return <button onClick={onClick} disabled={disabled} style={{ background: s.bg, color: s.cl, border: s.bd, borderRadius: 10, padding: "10px 18px", fontSize: 13.5, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1, fontFamily: "inherit" }}>{children}</button>;
};

const EXAMPLES = [
  "Short NIFTY ATM straddle at 9:20, exit 15:15, 30% SL per leg.",
  "Sell BANKNIFTY strangle at 0.3 delta CE and PE, 25% SL, re-enter on SL 2 times.",
  "Mera 'Delta Neutral' strategy ka SL 25% se 30% kar do.",
  "Meri saari strategies list karo aur batao kaunsi deployed hain.",
];

// Turn the raw Anthropic message array into an ordered list of renderable turns
// (mirrors AdminPanel.jsx's flattenAiCmdTranscript — same message shape, since
// both agents use the same tool-use loop pattern).
function flattenTranscript(messages) {
  const toolMeta = {};
  const turns = [];
  for (const m of messages || []) {
    if (m.role === "user") {
      if (typeof m.content === "string") turns.push({ type: "user_prompt", text: m.content });
      else if (Array.isArray(m.content)) {
        for (const block of m.content) {
          if (block.type === "tool_result") {
            const meta = toolMeta[block.tool_use_id] || {};
            turns.push({ type: "tool_call", name: meta.name || "?", input: meta.input, result: block.content });
          }
        }
      }
    } else if (m.role === "assistant" && Array.isArray(m.content)) {
      for (const block of m.content) {
        if (block.type === "text" && block.text) turns.push({ type: "assistant_text", text: block.text });
        else if (block.type === "tool_use") toolMeta[block.id] = { name: block.name, input: block.input };
      }
    }
  }
  return turns;
}

export default function StrategyTerminal() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState({});
  const pollRef = React.useRef(null);
  const bottomRef = React.useRef(null);

  const loadSessions = (selectFirst) => {
    strategyChatAPI.sessions().then((r) => {
      setSessions(r.data);
      if (selectFirst && r.data.length) selectSession(r.data[0]._id);
    }).catch((e) => setErr(e?.response?.data?.message || e.message));
  };
  useEffect(() => { loadSessions(true); /* eslint-disable-line */ }, []);
  useEffect(() => () => clearTimeout(pollRef.current), []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [session, job]);

  const selectSession = (id) => {
    setSessionId(id);
    strategyChatAPI.session(id).then((r) => setSession(r.data)).catch((e) => setErr(e?.response?.data?.message || e.message));
  };

  const newSession = async () => {
    setErr(""); setJob(null);
    try {
      const r = await strategyChatAPI.newSession();
      setSession({ _id: r.data.sessionId, title: "", messages: [] });
      setSessionId(r.data.sessionId);
      loadSessions(false);
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
  };

  const poll = (id) => {
    strategyChatAPI.job(id).then((r) => {
      setJob(r.data);
      if (r.data.status === "running") {
        pollRef.current = setTimeout(() => poll(id), 2000);
      } else {
        setBusy(false);
        selectSession(sessionId);
        loadSessions(false);
      }
    }).catch(() => setBusy(false));
  };

  const send = async (text) => {
    const t = (text ?? prompt).trim();
    if (!t || busy) return;
    setErr(""); setJob(null); setBusy(true);
    try {
      let sid = sessionId;
      if (!sid) {
        const r = await strategyChatAPI.newSession();
        sid = r.data.sessionId;
        setSessionId(sid);
      }
      setPrompt("");
      const r = await strategyChatAPI.run(sid, t);
      poll(r.data.jobId);
    } catch (e) { setErr(e?.response?.data?.message || e.message); setBusy(false); }
  };

  const turns = flattenTranscript(session?.messages);

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 20 }}>💬</span>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: T.text }}>Strategy Terminal</div>
          <div style={{ fontSize: 12, color: T.muted }}>Baat-cheet karke apni strategy banao ya modify karo.</div>
        </div>
      </div>

      <Card style={{ marginBottom: 14, border: "1px solid #c7d2fe", background: "linear-gradient(180deg,#eef2ff,#fff)" }}>
        <div style={{ fontSize: 12.5, color: "#3730a3", lineHeight: 1.6 }}>
          🔒 Ye sirf <b>aapki apni</b> strategies create/edit/backtest/delete kar sakta hai — kisi doosre user ya admin ke account ko kabhi touch nahi kar sakta. Aur ye seedha kisi strategy ko live/deploy nahi karta — jo bhi banaye ya badle, review karke Deploy aapko khud dabana hoga, bilkul us tarah jaise "Create a Strategy with a Prompt" mein hota hai.
        </div>
      </Card>

      <div style={{ display: "flex", gap: 14 }}>
        <Card style={{ padding: 10, width: 200, flexShrink: 0, height: 520, overflowY: "auto" }}>
          <div style={{ marginBottom: 8 }}><Btn onClick={newSession}>+ New chat</Btn></div>
          {sessions.map((s) => (
            <div key={s._id} onClick={() => selectSession(s._id)}
              style={{ padding: "8px 9px", borderRadius: 8, marginBottom: 4, cursor: "pointer", fontSize: 12, background: s._id === sessionId ? "#eef2ff" : "transparent", color: s._id === sessionId ? "#4338ca" : "#334155", fontWeight: s._id === sessionId ? 700 : 500 }}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title || "(untitled)"}</div>
              <div style={{ color: "#94a3b8", fontSize: 10.5 }}>{fmtDateTime(s.updatedAt)}</div>
            </div>
          ))}
          {sessions.length === 0 && <div style={{ color: "#94a3b8", fontSize: 12, padding: 6 }}>Koi chat nahi hai abhi. Neeche se pehla message bhejo.</div>}
        </Card>

        <div style={{ flex: 1, minWidth: 0 }}>
          <Card style={{ padding: 16, height: 420, overflowY: "auto", marginBottom: 10 }}>
            {turns.length === 0 && (
              <div>
                <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 10 }}>Kuch bhi likho — strategy banao, ya apni existing strategy ko badlo:</div>
                <div style={{ display: "grid", gap: 6 }}>
                  {EXAMPLES.map((ex, i) => (
                    <div key={i} onClick={() => send(ex)} style={{ cursor: "pointer", fontSize: 12.5, color: "#4338ca", background: "#eef2ff", border: "1px solid #e0e7ff", borderRadius: 8, padding: "7px 10px" }}>{ex}</div>
                  ))}
                </div>
              </div>
            )}
            {turns.map((t, i) => t.type === "user_prompt" ? (
              <div key={i} style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <div style={{ background: T.blue, color: "#fff", padding: "8px 12px", borderRadius: "12px 12px 2px 12px", maxWidth: "80%", fontSize: 13, whiteSpace: "pre-wrap" }}>{t.text}</div>
              </div>
            ) : t.type === "assistant_text" ? (
              <div key={i} style={{ display: "flex", justifyContent: "flex-start", marginBottom: 10 }}>
                <div style={{ background: "#f1f5f9", color: "#0f172a", padding: "8px 12px", borderRadius: "12px 12px 12px 2px", maxWidth: "80%", fontSize: 13, whiteSpace: "pre-wrap" }}>{t.text}</div>
              </div>
            ) : (
              <div key={i} style={{ marginBottom: 8 }}>
                <div onClick={() => setExpanded((e) => ({ ...e, [i]: !e[i] }))} style={{ cursor: "pointer", fontFamily: "monospace", fontSize: 11.5, color: "#7c3aed", background: "#faf5ff", border: "1px solid #ede9fe", borderRadius: 7, padding: "5px 9px", display: "inline-block" }}>
                  🔧 {t.name}({(t.input ? JSON.stringify(t.input) : "").slice(0, 70)}{t.input && JSON.stringify(t.input).length > 70 ? "…" : ""}) {expanded[i] ? "▲" : "▼"}
                </div>
                {expanded[i] && (
                  <pre style={{ background: "#0f172a", color: "#cbd5e1", borderRadius: 8, padding: 10, fontSize: 11, marginTop: 4, overflowX: "auto", maxHeight: 220 }}>
                    {typeof t.result === "string" ? t.result : JSON.stringify(t.result, null, 2)}
                  </pre>
                )}
              </div>
            ))}
            {job && job.status === "running" && (
              <div style={{ color: "#7c3aed", fontSize: 12, fontFamily: "monospace" }}>▶ {job.log[job.log.length - 1] || "working…"}</div>
            )}
            {job && job.status === "error" && (
              <div style={{ color: "#ef4444", fontSize: 12.5, marginTop: 6 }}>✗ {job.log[job.log.length - 1] || "Failed."}</div>
            )}
            <div ref={bottomRef} />
          </Card>

          {err && <div style={{ color: "#ef4444", fontSize: 12.5, marginBottom: 8 }}>{err}</div>}

          <div style={{ display: "flex", gap: 8 }}>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Likho… (Enter = send, Shift+Enter = nayi line)" rows={2}
              style={{ flex: 1, padding: "9px 11px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, resize: "vertical", fontFamily: "inherit" }} disabled={busy} />
            <Btn onClick={() => send()} disabled={busy || !prompt.trim()}>{busy ? "Chal raha hai…" : "Send"}</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
